import { useCallback, useEffect, useRef } from "react";
import { recordVideoAnalytics } from "../service/featured";
import { useAuth } from "../components/AuthProvider";

// Helper to get user ID from multiple sources
function getUserId(authDetails) {
  // First try authDetails
  if (authDetails?.id) {
    return authDetails.id;
  }
  // Fallback to localStorage
  try {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    if (authUser?.id) {
      return authUser.id;
    }
  } catch (e) {
    console.error("Failed to parse authUser from localStorage:", e);
  }
  return null;
}

function useVideoAnalytics() {
  const { authDetails } = useAuth();
  const userIdRef = useRef(getUserId(authDetails));
  const videoIdRef = useRef(null);

  const watchedDuration = useRef(0);

  const durationRecorder = useRef();

  // Keep userIdRef in sync with authDetails.id or localStorage
  useEffect(() => {
    userIdRef.current = getUserId(authDetails);
  }, [authDetails?.id]);

  // Get fresh user ID at record time to ensure we always have it
  const getCurrentUserId = useCallback(() => {
    return userIdRef.current || getUserId(authDetails);
  }, [authDetails]);

  const startRecordingDuration = useCallback((newVideoId) => {
    if (videoIdRef.current !== newVideoId) {
      if (videoIdRef.current) {
        const userId = getCurrentUserId();
        recordVideoAnalytics({
          id: videoIdRef.current,
          device: "web",
          duration: watchedDuration.current,
          created_by: userId,
        });
      }
      watchedDuration.current = 0;
      videoIdRef.current = newVideoId;
    }
    pauseRecordingDuration();
    resumeRecordingDuration();
  }, [getCurrentUserId]);

  const pauseRecordingDuration = useCallback(() => {
    clearInterval(durationRecorder.current);
  }, []);

  const resumeRecordingDuration = useCallback(() => {
    durationRecorder.current = setInterval(() => {
      watchedDuration.current += 1;
    }, 1000);
  }, []);

  const recordRecordingDuration = useCallback(() => {
    if (videoIdRef.current) {
      const userId = getCurrentUserId();
      recordVideoAnalytics({
        id: videoIdRef.current,
        device: "web",
        duration: watchedDuration.current,
        created_by: userId,
      });

      watchedDuration.current = 0;
      videoIdRef.current = null;
    }
  }, [getCurrentUserId]);

  useEffect(() => {
    window.addEventListener("beforeunload", recordRecordingDuration);
    return () => {
      window.removeEventListener("beforeunload", recordRecordingDuration);
    };
  }, []);

  useEffect(() => {
    return () => {
      recordRecordingDuration();
    };
  }, []);

  return {
    videoId: videoIdRef.current,
    start: startRecordingDuration,
    pause: pauseRecordingDuration,
    resume: resumeRecordingDuration,
    record: recordRecordingDuration,
  };
}

export default useVideoAnalytics;
